import React, { useState, useRef, useEffect } from 'react';
import { Logo } from '../components/Logo';
import { Link } from 'react-router-dom';

interface AdminNavBarProps {
  user: any; // You may want to properly type this based on your user type
  onSignOut: () => Promise<void>;
}

const AdminNavBar: React.FC<AdminNavBarProps> = ({ user, onSignOut }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  return (
    <div className="fixed top-0 left-0 w-full bg-white border-b shadow-sm z-50">
      <div className="flex justify-between items-center px-4 py-2">
        {/* Logo on the left */}
        <div className="flex items-center space-x-6">
          <Logo className="w-32" />
        </div>

        {/* Dashboard button and user dropdown on the right */}
        <div className="flex items-center space-x-4">
          <Link 
            to="/admin" 
            className="px-4 py-2 text-gray-700 hover:text-white hover:bg-[#F55D3E] rounded-md transition-colors font-medium"
          >
            Dashboard
          </Link>
          
          <Link 
            to="/admin/audit-logs" 
            className="px-4 py-2 text-gray-700 hover:text-white hover:bg-[#F55D3E] rounded-md transition-colors font-medium"
          >
            Audit Logs
          </Link>
          
          {/* User icon and dropdown on the right */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="flex items-center space-x-1 text-gray-700 hover:text-[#F55D3E] focus:outline-none"
            >
              <div className="relative">
                <div className="w-8 h-8 bg-[#F55D3E] text-white rounded-full flex items-center justify-center">
                  {user?.user_metadata.first_name?.[0]}{user?.user_metadata.last_name?.[0]}
                </div>
                {/* Admin badge */}
                <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  A
                </div>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-10">
                <div className="px-4 py-2 border-b">
                  <p className="font-medium">{user?.user_metadata.first_name} {user?.user_metadata.last_name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  <p className="text-xs text-[#F55D3E] mt-1">Administrator</p>
                </div>
                <button
                  onClick={() => {
                    setIsDropdownOpen(false);
                    onSignOut();
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-[#F55D3E] hover:bg-red-50 transition-colors duration-200"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNavBar; 